import { useCallback, useRef, useState } from 'react';

// Shared disclosure grammar: a compact record advances in place from scan, to
// peek, to full detail. Record-specific layouts and persistence stay in props.
function ProgressiveDisclosure({
    record,
    initialTier = 1,
    createDraft = item => item,
    onSave,
    renderTier1,
    renderTier2,
    renderTier3,
}) {
    const [tier, setTier] = useState(initialTier);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(() => createDraft(record));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const openedAt = useRef(0);

    const showPeek = useCallback(() => {
        openedAt.current = Date.now();
        setEditing(false);
        setTier(2);
    }, []);
    const showFull = useCallback(() => {
        openedAt.current = Date.now();
        setTier(3);
    }, []);
    const collapse = useCallback(() => {
        setEditing(false);
        setTier(initialTier);
    }, [initialTier]);
    const startEditing = useCallback(() => {
        setError(null);
        setDraft(createDraft(record));
        setEditing(true);
    }, [createDraft, record]);
    const cancelEditing = useCallback(() => {
        setError(null);
        setDraft(createDraft(record));
        setEditing(false);
    }, [createDraft, record]);
    const updateDraft = useCallback((key, value) => {
        setDraft(current => ({ ...current, [key]: value }));
    }, []);
    const save = useCallback(async () => {
        if (!onSave) return;
        setSaving(true);
        setError(null);
        try {
            await onSave(draft);
            setEditing(false);
        } catch (saveError) {
            setError(saveError.message || String(saveError));
        } finally {
            setSaving(false);
        }
    }, [draft, onSave]);

    const controls = {
        tier, showPeek, showFull, collapse, openedAt: openedAt.current,
        editing, draft, saving, error,
        startEditing, cancelEditing, updateDraft, save,
    };

    if (tier === 1) return renderTier1?.(controls) || null;
    if (tier === 2) return renderTier2?.(controls) || null;
    return renderTier3?.(controls) || null;
}

export default ProgressiveDisclosure;
